/**
 * Content for the six industry-page SEO story sections — Developer Handoff v3.7
 * §04 and §11.2–§11.10. H2s, ledes and bullets are verbatim from the handoff;
 * Directive's H2 wording must not be paraphrased.
 *
 * These are section *shells* in the same sense as the two new home sections: they
 * render through the normal typed-section path, but the DatoCMS records do not
 * exist yet, so each page injects the shell at its designed position. The shape is
 * deliberately identical to a seeded record — `contentItemEntries` with groupKey
 * "point" / "flow" — so seeding is a straight lift and the component needs no
 * separate fallback path. Once the records exist in Dato these can go (tracked
 * alongside T-061).
 *
 * Design-file hrefs are mapped to production routes per the handoff's §10 URL map.
 */

// Trailing slash is the canonical internal URL form (see @utils/internalHref). These
// are interpolated into hand-authored anchor markup that no normalizer sees, so the
// slash has to be part of the constant.
const HOW_IT_WORKS = "/how-it-works/"
const REVENUE_RECOVERY = "/revenue-recovery/"

export const agenciesSeoSections = [
  {
    sectionKey: "agencies_seo_how",
    componentType: "sxp_story",
    variant: "agencies_pipeline",
    theme: "",
    hidden: false,
    editorLabel: "How It Works (SEO)",
    eyebrow: "How it works",
    headingHtml: "How MoonClerk handles Stripe recurring payments <em>for agencies</em>",
    summary:
      "MoonClerk gives agencies a no-code way to run Stripe recurring payments — no custom billing flow to build.",
    contentItemEntries: [
      { groupKey: "point", rank: 1, body: "<b>Connect Stripe</b>, create a payment form for a retainer or subscription, choose the billing schedule" },
      { groupKey: "point", rank: 2, body: "<b>Send the branded link</b> — the client signs up, Stripe processes the payment" },
      { groupKey: "point", rank: 3, body: "<b>MoonClerk runs the billing workflow:</b> branded checkout, automatic renewals, reminder emails, failed-payment retries" },
      { groupKey: "point", rank: 4, body: "<b>Signed proposal → active retainer, faster</b> — recurring billing software without custom development" },
      { groupKey: "flow", rank: 1, title: "Connect Stripe" },
      { groupKey: "flow", rank: 2, title: "Create the retainer form" },
      { groupKey: "flow", rank: 3, title: "Choose the schedule" },
      { groupKey: "flow", rank: 4, title: "Send the branded link" },
    ],
  },
  {
    sectionKey: "agencies_seo_why",
    componentType: "sxp_story",
    variant: "agencies_run",
    theme: "flip alt2",
    hidden: false,
    editorLabel: "Why MoonClerk (SEO)",
    eyebrow: "Why MoonClerk",
    headingHtml: "Why agencies choose MoonClerk as <em>recurring billing software</em>",
    summary:
      "Stripe processes payments — MoonClerk makes recurring invoicing and subscription billing easier to run for service businesses.",
    contentItemEntries: [
      { groupKey: "point", rank: 1, body: "<b>Branded payment pages</b> your clients see as part of your agency" },
      { groupKey: "point", rank: 2, body: "<b>Automated recurring charges</b> collected on a set schedule" },
      { groupKey: "point", rank: 3, body: "<b>Multi-account billing</b> — manage activity across clients without engineering work" },
      { groupKey: "point", rank: 4, body: "<b>One workflow</b> replaces patched-together invoice tools, payment links, and follow-up emails" },
      { groupKey: "point", rank: 5, body: "<b>Fits the full book:</b> SEO retainers, paid media fees, onboarding deposits, monthly service subscriptions" },
    ],
  },
]

export const nonprofitsSeoSections = [
  {
    sectionKey: "nonprofits_seo_how",
    componentType: "sxp_story",
    variant: "nonprofits_giving",
    theme: "",
    hidden: false,
    editorLabel: "How It Works (SEO)",
    eyebrow: "How it works",
    headingHtml: "How Stripe recurring payments work <em>for nonprofits</em>",
    summary:
      "MoonClerk gives nonprofits a no-code way to accept Stripe recurring payments — no custom billing flow, no developer.",
    contentItemEntries: [
      { groupKey: "point", rank: 1, body: "<b>Connect Stripe</b> and create a branded donation form that feels like part of your site" },
      { groupKey: "point", rank: 2, body: "<b>Offer the giving options you want</b> — one-time, monthly, or yearly gifts" },
      { groupKey: "point", rank: 3, body: "<b>Donors choose amount + frequency</b> and pay through secure checkout tied to your Stripe setup" },
      { groupKey: "point", rank: 4, body: "<b>Recurring gifts run themselves</b> — payment details stay on file and follow the schedule you set" },
      { groupKey: "point", rank: 5, body: `<b>Launch monthly giving fast</b> — see <a href="${HOW_IT_WORKS}">how MoonClerk works</a> for the full setup path` },
      { groupKey: "flow", rank: 1, title: "Connect Stripe" },
      { groupKey: "flow", rank: 2, title: "Build your donation form" },
      { groupKey: "flow", rank: 3, title: "Choose giving options" },
      { groupKey: "flow", rank: 4, title: "Share it with donors" },
    ],
  },
  {
    sectionKey: "nonprofits_seo_donor",
    componentType: "sxp_story",
    variant: "nonprofits_portal",
    theme: "flip alt2",
    hidden: false,
    editorLabel: "Donor Self-Serve (SEO)",
    eyebrow: "Donor self-serve",
    headingHtml: "How donors manage their <em>recurring payments</em>",
    summary:
      "Recurring giving works best when donors can manage their gift without contacting your team for every update.",
    contentItemEntries: [
      { groupKey: "point", rank: 1, body: "<b>Donor self-serve:</b> update card details, change gift amounts, review payment history" },
      { groupKey: "point", rank: 2, body: "<b>Saved payment methods</b> — Stripe keeps recurring billing running without re-entry" },
      { groupKey: "point", rank: 3, body: `<b>Revenue protection built in:</b> card updates, pre-dunning, and <a href="${REVENUE_RECOVERY}">failed payment recovery</a>` },
      { groupKey: "point", rank: 4, body: "<b>Less friction, longer giving</b> — supporters who need a quick change keep giving" },
    ],
  },
]

export const coachesSeoSections = [
  {
    sectionKey: "coaches_seo_category",
    componentType: "sxp_story",
    variant: "coaches_onesystem",
    theme: "",
    hidden: false,
    editorLabel: "Category Explainer (SEO)",
    eyebrow: "The category, explained",
    headingHtml:
      "What recurring billing software should handle <em>for a coaching business</em>",
    summary:
      "Recurring billing software should charge clients on schedule — without rebuilding the same invoice process every month.",
    contentItemEntries: [
      { groupKey: "point", rank: 1, body: "<b>Every way you charge, one system:</b> monthly retainers, fixed-length packages billed over time, one-time sessions" },
      { groupKey: "point", rank: 2, body: "<b>Admin handled:</b> receipts, failed-payment follow-up, and client self-service built in" },
      { groupKey: "point", rank: 3, body: "<b>Simple to launch:</b> create payment links or package pages, collect through Stripe" },
      { groupKey: "point", rank: 4, body: "<b>Same goal every time</b> — clear billing for the client, less manual follow-up for you" },
    ],
  },
  {
    sectionKey: "coaches_seo_invoices",
    componentType: "sxp_story",
    variant: "coaches_setonce",
    theme: "flip alt2",
    hidden: false,
    editorLabel: "Recurring Invoices (SEO)",
    eyebrow: "Recurring invoices",
    headingHtml: "How recurring invoice software works <em>with Stripe payments</em>",
    summary:
      "Set the amount, cadence, and package terms once — the billing runs automatically from there.",
    contentItemEntries: [
      { groupKey: "point", rank: 1, body: "<b>No fresh invoice every cycle</b> — clients pay on schedule without being chased" },
      { groupKey: "point", rank: 2, body: "<b>Fits coaching revenue:</b> monthly retainers, installment packages, ongoing advisory" },
      { groupKey: "point", rank: 3, body: "<b>Stripe as the payment layer:</b> recurring card and ACH payments, no custom development" },
      { groupKey: "point", rank: 4, body: "<b>Renewals stay hands-off:</b> receipts go out automatically, failed cards are retried" },
      { groupKey: "flow", rank: 1, title: "Set amount & cadence once" },
      { groupKey: "flow", rank: 2, title: "Client picks a package" },
      { groupKey: "flow", rank: 3, title: "Billing runs on schedule" },
      { groupKey: "flow", rank: 4, title: "Receipts & retries automatic" },
    ],
  },
]
